/**
 * Catalog Sync dashboard for Magento / Adobe Commerce.
 *
 * Shows current sync state, staleness warning (>25 hours since last full sync),
 * manual trigger button, and a reset button for stuck syncs.
 *
 * Calls existing /api/magento/:siteId/sync-status, /sync/trigger, /sync/reset
 * (backend-merged/routes/magento.js).
 *
 * PRD: cross-cutting/prds/2026-06-12-I4-magento-adobe-commerce/prd.md §6
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Clock,
  RotateCcw,
  Database,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSite } from '@/contexts/SiteContext';
import { useToast } from '@/components/ui/use-toast';
import {
  getMagentoSyncStatus,
  triggerMagentoSync,
  resetMagentoSync,
  MagentoNotConfiguredError,
  type MagentoSyncState,
} from '@/services/magentoIntegrationService';

const STALE_HOURS = 25;

const STATUS_CONFIG: Record<
  string,
  { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string; icon: React.ReactNode }
> = {
  idle:        { variant: 'secondary',   label: 'Idle',        icon: <Clock className="h-3.5 w-3.5" /> },
  full_sync:   { variant: 'default',     label: 'Full sync',   icon: <Loader2 className="h-3.5 w-3.5 animate-spin" /> },
  incremental: { variant: 'default',     label: 'Incremental', icon: <Loader2 className="h-3.5 w-3.5 animate-spin" /> },
  error:       { variant: 'destructive', label: 'Error',       icon: <AlertCircle className="h-3.5 w-3.5" /> },
};

function formatRelative(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function isStale(dateStr: string | null): boolean {
  if (!dateStr) return true;
  return Date.now() - new Date(dateStr).getTime() > STALE_HOURS * 3_600_000;
}

export default function MagentoCatalogSync() {
  const { selectedSite } = useSite();
  const { toast } = useToast();
  const siteId = selectedSite?.id || (selectedSite as any)?._id;

  const [state, setState] = useState<MagentoSyncState | null>(null);
  const [loading, setLoading] = useState(true);
  const [notConfigured, setNotConfigured] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [triggering, setTriggering] = useState(false);
  const [triggerFull, setTriggerFull] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetting, setResetting] = useState(false);

  const loadAbortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    if (!siteId) return;
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    const loadedForSiteId = siteId;

    setError(null);
    try {
      const data = await getMagentoSyncStatus(loadedForSiteId, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setState(data);
      setNotConfigured(false);
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      if (err instanceof MagentoNotConfiguredError) {
        setNotConfigured(true);
      } else {
        setError(err.message);
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [siteId]);

  // Cancel in-flight load on unmount.
  useEffect(() => () => { loadAbortRef.current?.abort(); }, []);

  // On site change: abort in-flight load, reset all state so stale site data
  // is never shown under the newly selected site.
  useEffect(() => {
    loadAbortRef.current?.abort();
    setState(null);
    setNotConfigured(false);
    setError(null);
    setLoading(true);
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  // Poll when a sync is in progress
  useEffect(() => {
    if (!state) return;
    if (state.sync_status !== 'full_sync' && state.sync_status !== 'incremental') return;
    const interval = setInterval(load, 8000);
    return () => clearInterval(interval);
  }, [state, load]);

  if (!siteId) {
    return (
      <div className="p-8">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Select a site to view catalog sync status.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const handleTrigger = async (full: boolean) => {
    setTriggering(true);
    setTriggerFull(full);
    try {
      const result = await triggerMagentoSync(siteId, { full });
      toast({
        title: full ? 'Full sync started' : 'Incremental sync started',
        description: `Mode: ${result.mode}`,
      });
      setTimeout(load, 2000);
    } catch (err: any) {
      toast({ title: 'Sync failed', description: err.message, variant: 'destructive' });
    } finally {
      setTriggering(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const result = await resetMagentoSync(siteId);
      if (result.reset) {
        toast({ title: 'Sync reset', description: 'Sync state returned to idle. Cursor cleared.' });
      } else {
        toast({ title: 'Nothing to reset', description: 'Sync was not stuck.' });
      }
      setShowResetDialog(false);
      load();
    } catch (err: any) {
      toast({ title: 'Reset failed', description: err.message, variant: 'destructive' });
    } finally {
      setResetting(false);
    }
  };

  const syncBusy = state?.sync_status === 'full_sync' || state?.sync_status === 'incremental';
  const syncStuck = syncBusy;
  const staleWarning = isStale(state?.last_full_sync_at ?? null);
  const cfg = state ? (STATUS_CONFIG[state.sync_status] ?? STATUS_CONFIG['idle']) : null;

  return (
    <div className="p-8 max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Database className="h-6 w-6" />
          Catalog Sync
        </h1>
        <p className="text-muted-foreground mt-1">
          Monitor and control Magento product catalog synchronization.
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {notConfigured && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Magento connection not configured for this site. Go to{' '}
            <strong>Magento / Adobe Commerce</strong> settings to add credentials.
          </AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-36 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : state ? (
        <>
          {/* Status card */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Sync Status</CardTitle>
                {cfg && (
                  <Badge variant={cfg.variant} className="flex items-center gap-1 text-xs">
                    {cfg.icon}
                    {cfg.label}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Last full sync</p>
                  <p className="font-medium flex items-center gap-1">
                    {staleWarning && <AlertCircle className="h-3.5 w-3.5 text-amber-500" />}
                    {formatRelative(state.last_full_sync_at)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Last incremental</p>
                  <p className="font-medium">{formatRelative(state.last_incremental_sync_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Resume page</p>
                  <p className="font-medium">{state.last_sync_page}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Resume SKU</p>
                  <p className="font-mono text-xs truncate">{state.last_sync_sku ?? '—'}</p>
                </div>
              </div>

              {state.error_message && (
                <Alert variant="destructive" className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-xs font-mono">{state.error_message}</AlertDescription>
                </Alert>
              )}

              {staleWarning && !state.error_message && (
                <Alert className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Last full sync was over {STALE_HOURS}h ago. Consider triggering a full sync.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Actions</CardTitle>
              <CardDescription>Manual sync controls — scheduled incremental runs every 15 min automatically.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleTrigger(false)}
                  disabled={triggering || syncBusy}
                >
                  {triggering && !triggerFull ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting…</>
                  ) : (
                    <><RefreshCw className="h-4 w-4 mr-2" /> Incremental sync</>
                  )}
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleTrigger(true)}
                  disabled={triggering || syncBusy}
                >
                  {triggering && triggerFull ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting…</>
                  ) : (
                    <><Database className="h-4 w-4 mr-2" /> Full sync</>
                  )}
                </Button>
                {syncStuck && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowResetDialog(true)}
                  >
                    <RotateCcw className="h-4 w-4 mr-2" /> Reset stuck sync
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
                  <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}

      {/* Reset confirmation */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Stuck Sync?</DialogTitle>
            <DialogDescription>
              This sets the sync status back to idle and clears the resume cursor (page + SKU),
              so the next sync restarts from page 1. Use only when a sync appears permanently stuck.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReset} disabled={resetting}>
              {resetting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Resetting…</>
              ) : (
                'Reset Sync'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
